// MyStatsPage — May 4 2026 (v2.5.70)
// ─────────────────────────────────────────────────────────────────────────────
// Player-scoped stats. Shows ONLY the current user's data:
//   • Headline cards: games played, attendance %, win rate, current streak
//   • Personal achievements (badges)
//   • Per-bucket counts: ✅ in, ❌ out, 🤔 maybe
//   • Where they rank among teammates (games attended)
//
// Distinct from the coach StatsPage (which shows team-wide leaderboards,
// AI logs, feedback, season summaries). This route is added to the player
// nav in v2.5.70 alongside hiding "Team" entirely from non-coach nav.
//
// Coaches can also reach this page (via direct URL) but their nav points
// to /stats not /my-stats so they'll rarely land here. The data is
// user-scoped from the backend regardless of role, so a coach who does
// visit just sees their own personal stats.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Trophy, TrendingUp, Calendar, Star, BarChart2, Award, Target, Activity } from 'lucide-react';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import toast from 'react-hot-toast';
import { PageSpinner } from '../components/Skeleton.jsx';
// 2026-07-27 — expandable per-player mini-game detail, shared with the
// coach StatsPage so both leaderboards show identical receipts.
import PlayerGameLog from '../components/PlayerGameLog.jsx';

export default function MyStatsPage() {
  const { teamId } = useParams();
  const { user }   = useAuth();
  const [achievements, setAchievements] = useState(null);
  const [leaderboard,  setLeaderboard]  = useState([]);
  const [goalieStats,  setGoalieStats]  = useState(null); // { swapEnabled, goalies: [] }
  const [loading, setLoading] = useState(true);
  // 2026-07-27 (per Gilles) — players now see the FULL team leaderboard
  // here (sortable), just without rank numbers/medals.
  // 2026-08-03 (per Gilles) — PLAYER default sort is Best +/− (chip
  // first, left of Most Wins). The coach Stats page keeps Most Wins
  // as its default — this is the player-view preference only.
  const [lbSort, setLbSort] = useState('plusMinus');
  // 2026-07-27 — which team-leaderboard row is expanded to its
  // game-by-game detail (player user id, null = none).
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [aRes, lRes, gRes] = await Promise.all([
          api.get(`/teams/${teamId}/stats/achievements`).catch(() => ({ data: null })),
          api.get(`/teams/${teamId}/stats/leaderboard`).catch(() => ({ data: [] })),
          // Goalie section (same data the coach Stats page shows;
          // pre-deploy backends 404 → section simply hidden).
          api.get(`/teams/${teamId}/stats/goalies`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setAchievements(aRes.data || { stats: {}, badges: [] });
        setLeaderboard(lRes.data || []);
        setGoalieStats(gRes.data);
      } catch {
        if (!cancelled) toast.error('Failed to load your stats');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  if (loading) return <PageSpinner />;

  // Find this user's leaderboard row. The leaderboard endpoint returns
  // every roster member; we filter to ours. If the user isn't in the
  // list yet (brand-new join, no games yet), fall back to zeros.
  const myRow = leaderboard.find(p => p.id === user?.id) || {
    name: user?.name || 'You',
    gamesIn: 0, gamesOut: 0, gamesMaybe: 0, gamesPlayed: 0, wins: 0, losses: 0, ties: 0,
    goalsFor: 0, goalsAgainst: 0, plusMinus: 0,
  };

  // 2026-07-25 — per-user tile visibility (Profile → My Stats Tiles,
  // mig 107). Only an explicit false hides a tile; missing key = show,
  // so new tiles are visible by default for everyone.
  const showTile = (key) => user?.statsTilePrefs?.[key] !== false;

  // ── 2026-07-27 (per Gilles) — TEAM LEADERBOARD, player edition. ──
  // Everyone's stats, sortable, but NO rank numbers or medals — the
  // ranking stays a coach-page thing; here it's "how is everyone
  // doing", not "who beats whom". Same visibility rules as the coach
  // leaderboard: goalies live in their own section below, and players
  // who never played (and never RSVPed in) are hidden.
  const boardPlayers = leaderboard.filter(
    p => p.position !== 'goalie' &&
         ((p.gamesPlayed || 0) > 0 || (p.gamesIn || 0) > 0)
  );
  const winPctOf = (p) => {
    const dec = (p.wins || 0) + (p.losses || 0) + (p.ties || 0);
    return dec > 0 ? Math.round(((p.wins || 0) / dec) * 100) : null;
  };
  const teamBoardSection = boardPlayers.length > 0 && (
    <div className="zen-card p-5 mb-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy size={14} style={{ color: 'var(--puck)' }} />
          <h2 className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: 'var(--font-display)' }}>
            Team Leaderboard
          </h2>
        </div>
      </div>
      {/* Sort chips — 2026-08-03 (per Gilles): Best +/− leads and is
          the default in the PLAYER view. */}
      <div className="flex gap-2 flex-wrap mb-3">
        {/* 2026-09-17 (per Gilles) — same chips as the coach board:
            Best +/− stays the player default, then Wins, Most checked
            in, No-shows (flagged players only). */}
        {[
          { key: 'plusMinus',   label: '➕ Best +/−'        },
          { key: 'wins',        label: '🏆 Wins'            },
          { key: 'gamesIn',     label: '✅ Most checked in' },
          { key: 'noShows',     label: '👎 No-shows'        },
        ].map(s => (
          <button key={s.key} onClick={() => setLbSort(s.key)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: lbSort === s.key ? 'var(--ice-a12)' : 'var(--surface-2)',
              border: `1px solid ${lbSort === s.key ? 'var(--ice-a30)' : 'var(--border)'}`,
              color: lbSort === s.key ? 'var(--ice)' : 'var(--text-2)',
            }}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {[...boardPlayers]
          .filter(p => lbSort === 'noShows' ? (p.noShows || 0) > 0 : true)
          .sort((a, b) => (b[lbSort] || 0) - (a[lbSort] || 0))
          .map(p => {
            const isMe = p.id === user?.id;
            const wp = winPctOf(p);
            // 2026-09-17 — stat privacy: hidden tiles show a lock for
            // everyone but the owner (backend nulls the values too).
            const hid = (k) => Array.isArray(p.hidden) && p.hidden.includes(k);
            const LOCK = <span title="This player keeps this stat private">🔒</span>;
            return (
              <div key={p.id} className="rounded-xl p-3 flex items-center gap-3 flex-wrap"
                onClick={() => setExpandedPlayerId(prev => prev === p.id ? null : p.id)}
                title="Tap to see the game-by-game results behind these numbers"
                style={{
                  background: isMe ? 'var(--ice-a8)' : 'var(--surface-2)',
                  border: `1px solid ${isMe ? 'var(--ice-a30)' : 'var(--border)'}`,
                  cursor: 'pointer',
                }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                  {p.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-sm truncate">
                    {p.name}{isMe ? ' (you)' : ''}
                  </span>
                  {p.position && (
                    <span className="text-xs text-secondary capitalize ml-2">{p.position}</span>
                  )}
                </div>
                <div className="flex gap-3 flex-shrink-0 text-center w-full sm:w-auto justify-around sm:justify-end mt-1 sm:mt-0">
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'var(--puck)', fontFamily: 'var(--font-display)' }}>
                      {hid('record') ? LOCK : <>{p.wins || 0}-{p.losses || 0}{(p.ties || 0) > 0 ? `-${p.ties}` : ''}</>}
                    </div>
                    <div style={{ fontSize: 10 }} className="text-secondary">{(p.ties || 0) > 0 ? 'W-L-T' : 'W-L'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'var(--goal)', fontFamily: 'var(--font-display)' }}>
                      {(hid('record') || hid('winRate')) ? LOCK : wp !== null ? `${wp}%` : '—'}
                    </div>
                    <div style={{ fontSize: 10 }} className="text-secondary">Win %</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'var(--ice)', fontFamily: 'var(--font-display)' }}>{hid('gamesPlayed') ? LOCK : (p.gamesPlayed || 0)}</div>
                    <div style={{ fontSize: 10 }} className="text-secondary">Played</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
                      {hid('goals') ? LOCK : <>{p.goalsFor || 0}<span style={{ color: 'var(--text-3)', fontWeight: 400 }}>·</span>{p.goalsAgainst || 0}</>}
                    </div>
                    <div style={{ fontSize: 10 }} className="text-secondary">GF·GA</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold" style={{
                      fontFamily: 'var(--font-display)',
                      color: (p.plusMinus || 0) > 0 ? 'var(--goal)' : (p.plusMinus || 0) < 0 ? 'var(--penalty)' : 'var(--text-2)',
                    }}>
                      {hid('plusMinus') ? LOCK : <>{(p.plusMinus || 0) > 0 ? '+' : ''}{p.plusMinus || 0}</>}
                    </div>
                    <div style={{ fontSize: 10 }} className="text-secondary">+/−</div>
                  </div>
                  {/* 2026-07-28 (mig 109) — season ghost count; renders
                      only for players who've actually no-showed. */}
                  {(p.noShows || 0) > 0 && (
                    <div>
                      <div className="text-sm font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--puck)' }}>
                        {p.noShows}
                      </div>
                      <div style={{ fontSize: 10 }} className="text-secondary">👎 No-show</div>
                    </div>
                  )}
                </div>
                {/* 2026-07-27 — expanded game-by-game detail. */}
                {expandedPlayerId === p.id && (
                  <div className="w-full" onClick={e => e.stopPropagation()}>
                    <PlayerGameLog teamId={teamId} userId={p.id} />
                  </div>
                )}
              </div>
            );
          })}
      </div>
      {/* Goalies — same section the coach page shows, medal-free. */}
      {goalieStats?.goalies?.length > 0 && (
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-bold text-xs uppercase tracking-wider" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-2)' }}>
              🥅 Goalies
            </h3>
            {goalieStats.swapEnabled && (
              <span className="text-xs px-2 py-0.5 rounded-md"
                style={{ background: 'var(--ice-a10)', color: 'var(--ice)', border: '1px solid var(--ice-a20)' }}>
                Sides swap each mini-game
              </span>
            )}
          </div>
          <div className="space-y-2">
            {goalieStats.goalies.map(g => (
              <div key={g.id} className="rounded-xl p-3 flex items-center gap-3 flex-wrap"
                onClick={() => setExpandedPlayerId(prev => prev === g.id ? null : g.id)}
                title="Tap to see the game-by-game results behind these numbers"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
                  style={{ background: 'var(--ice-a12)', border: '1px solid var(--ice-a30)', color: 'var(--ice)' }}>
                  {g.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-sm truncate">{g.name}</span>
                </div>
                <div className="flex gap-3 flex-shrink-0 text-center">
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'var(--puck)', fontFamily: 'var(--font-display)' }}>
                      {g.wins == null ? '🔒' : <>{g.wins}-{g.losses}{g.ties > 0 ? `-${g.ties}` : ''}</>}
                    </div>
                    <div style={{ fontSize: 10 }} className="text-secondary">{g.ties > 0 ? 'W-L-T' : 'W-L'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'var(--ice)', fontFamily: 'var(--font-display)' }}>{g.gamesPlayed == null ? '🔒' : g.gamesPlayed}</div>
                    <div style={{ fontSize: 10 }} className="text-secondary">Played</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
                      {g.goalsFor == null ? '🔒' : <>{g.goalsFor}<span style={{ color: 'var(--text-3)', fontWeight: 400 }}>·</span>{g.goalsAgainst}</>}
                    </div>
                    <div style={{ fontSize: 10 }} className="text-secondary">GF·GA</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold" style={{ color: 'var(--goal)', fontFamily: 'var(--font-display)' }}>
                      {g.gaa !== null && g.gaa !== undefined ? g.gaa : '—'}
                    </div>
                    <div style={{ fontSize: 10 }} className="text-secondary">GA/game</div>
                  </div>
                </div>
                {/* 2026-07-27 — expanded game-by-game detail (swap-aware). */}
                {expandedPlayerId === g.id && (
                  <div className="w-full" onClick={e => e.stopPropagation()}>
                    <PlayerGameLog teamId={teamId} userId={g.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Where do I rank by games attended? 1-indexed. Ties broken by wins
  // (matches the backend ORDER BY in the leaderboard query so the rank
  // here matches the order in StatsPage's leaderboard tab).
  const myRank = (() => {
    const sorted = [...leaderboard].sort((a, b) => {
      if (b.gamesIn !== a.gamesIn) return b.gamesIn - a.gamesIn;
      return (b.wins || 0) - (a.wins || 0);
    });
    const idx = sorted.findIndex(p => p.id === user?.id);
    return idx >= 0 ? idx + 1 : null;
  })();
  const teammateCount = leaderboard.length;

  const s = achievements?.stats || {};
  const totalGames     = s.totalGames     ?? 0;
  const totalIn        = s.totalIn        ?? 0;
  const attendancePct  = s.attendancePct  ?? 0;
  const currentStreak  = s.currentStreak  ?? 0;
  const maxStreak      = s.maxStreak      ?? 0;
  const winRate        = s.winRate        ?? 0;
  const wins           = s.wins           ?? 0;
  const losses         = s.losses         ?? 0;
  const winStreak      = s.winStreak      ?? 0;
  const maxWinStreak   = s.maxWinStreak   ?? 0;
  const badges         = achievements?.badges || [];

  // Empty state — brand new player, hasn't played anything yet.
  if (totalGames === 0 && myRow.gamesIn === 0 && myRow.gamesOut === 0 && myRow.gamesMaybe === 0) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 720 }}>
        <div className="flex items-center gap-3 mb-6">
          <BarChart2 size={20} style={{ color: 'var(--ice)' }} />
          <h1 className="font-display font-bold text-xl uppercase tracking-wider">My Stats</h1>
        </div>
        <div className="zen-card p-8 text-center mb-5">
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏒</div>
          <h2 className="font-bold text-lg mb-2">Nothing to show yet</h2>
          <p className="text-sm text-secondary mb-1" style={{ lineHeight: 1.5 }}>
            Once you start RSVPing to games, your attendance, win rate, and
            achievements will show up here.
          </p>
          <p className="text-xs text-secondary mt-3" style={{ opacity: 0.7 }}>
            Stats only count games on this team's schedule.
          </p>
        </div>
        {/* 2026-07-27 — even with no personal stats yet, the team
            leaderboard is worth a look. */}
        {teamBoardSection}
      </div>
    );
  }

  // Headline stat cards. Color-coded so the eye lands on the most
  // notable number first (streak when active, win rate when not).
  // 2026-07-25 (per Gilles) — expanded so players see everything the
  // coach sees about them: W-L record, GF·GA, and +/- join the original
  // four. Each carries a `key` matched against statsTilePrefs so the
  // player can hide any tile from Profile → My Stats Tiles.
  // 2026-07-27 (per Gilles) — default tile ORDER: Win Rate first,
  // Plus/Minus second, Games Played third, then the rest.
  const headline = [
    {
      key: 'winRate',
      label: 'Win Rate', value: (wins + losses) > 0 ? `${winRate}%` : '—',
      sub: (wins + losses) > 0 ? `${wins}W ${losses}L${myRow.ties > 0 ? ` ${myRow.ties}T` : ''}` : 'No results recorded yet',
      icon: Trophy, color: 'var(--puck)',
    },
    {
      key: 'plusMinus',
      label: 'Plus / Minus',
      value: `${(myRow.plusMinus || 0) > 0 ? '+' : ''}${myRow.plusMinus || 0}`,
      sub: 'Goal differential across your games',
      icon: TrendingUp,
      color: (myRow.plusMinus || 0) > 0 ? 'var(--goal)'
           : (myRow.plusMinus || 0) < 0 ? 'var(--penalty)' : 'var(--text-3)',
    },
    {
      key: 'gamesPlayed',
      label: 'Games Played', value: myRow.gamesPlayed, sub: `${totalIn} attended of ${totalGames} on schedule`,
      icon: Calendar, color: 'var(--ice)',
    },
    {
      key: 'record',
      label: 'My Record',
      value: `${myRow.wins || 0}-${myRow.losses || 0}${(myRow.ties || 0) > 0 ? `-${myRow.ties}` : ''}`,
      sub: ((myRow.wins || 0) + (myRow.losses || 0) + (myRow.ties || 0)) > 0
        ? ((myRow.ties || 0) > 0 ? 'Wins-Losses-Ties' : 'Wins-Losses')
        : 'No results recorded yet',
      icon: Award, color: 'var(--puck)',
    },
    {
      key: 'goals',
      label: 'Goals For · Against',
      value: `${myRow.goalsFor || 0} · ${myRow.goalsAgainst || 0}`,
      sub: "Your teams' goals while you played",
      icon: Target, color: 'var(--goal)',
    },
    {
      key: 'attendance',
      label: 'Attendance', value: `${attendancePct}%`,
      sub: totalGames > 0 ? `${totalIn} of ${totalGames} games` : '—',
      icon: Activity, color: 'var(--goal)',
    },
    {
      key: 'streak',
      label: 'Current Streak',
      value: currentStreak > 0 ? `${currentStreak}` : '0',
      sub: maxStreak > 0 ? `Best: ${maxStreak}` : 'Show up next game to start one',
      icon: TrendingUp, color: currentStreak > 0 ? 'var(--fire, #ff6b35)' : 'var(--text-3)',
    },
  ].filter(h => showTile(h.key));

  return (
    <div className="animate-fade-in" style={{ maxWidth: 720 }}>
      <div className="flex items-center gap-3 mb-6">
        <BarChart2 size={20} style={{ color: 'var(--ice)' }} />
        <h1 className="font-display font-bold text-xl uppercase tracking-wider">My Stats</h1>
      </div>

      {/* ── Headline cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {headline.map((h) => {
          const Icon = h.icon;
          return (
            <div key={h.label} className="zen-card p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Icon size={13} style={{ color: h.color }} />
                <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--text-3)' }}>
                  {h.label}
                </span>
              </div>
              <div className="text-2xl font-bold" style={{ color: h.color, fontFamily: 'var(--font-display)', lineHeight: 1.1 }}>
                {h.value}
              </div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-2)', lineHeight: 1.4 }}>
                {h.sub}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Your results (2026-09-17, per Gilles) ──
          Replaces the RSVP Breakdown (In / Out / Maybe) with the same
          "Your results" strip the coach Stats page shows: Checked in,
          Record, GF·GA, +/− (+ no-shows if any). The Profile toggle key
          stays 'rsvp' so existing preferences keep working. ── */}
      {showTile('rsvp') && (
      <div className="zen-card p-5 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Target size={14} style={{ color: 'var(--ice)' }} />
          <h2 className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: 'var(--font-display)' }}>
            Your results
          </h2>
        </div>
        <p className="text-xs text-secondary mb-3">Mini-game record from the games you played</p>
        <div className="flex items-center justify-around flex-wrap gap-3">
          {[
            ['Checked in', myRow.gamesIn ?? 0, 'var(--text)'],
            ['Record', `${myRow.wins ?? 0}-${myRow.losses ?? 0}${(myRow.ties ?? 0) > 0 ? `-${myRow.ties}` : ''}`, 'var(--goal)'],
            ['GF · GA', `${myRow.goalsFor ?? 0} · ${myRow.goalsAgainst ?? 0}`, 'var(--text)'],
            ['+/−', `${(myRow.plusMinus ?? 0) > 0 ? '+' : ''}${myRow.plusMinus ?? 0}`,
              (myRow.plusMinus ?? 0) > 0 ? 'var(--goal)' : (myRow.plusMinus ?? 0) < 0 ? 'var(--penalty)' : 'var(--text)'],
            ...((myRow.noShows ?? 0) > 0 ? [['👎 No-shows', myRow.noShows, 'var(--puck)']] : []),
          ].map(([label, val, color]) => (
            <div key={label} style={{ textAlign: 'center', minWidth: 64 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color, fontFamily: 'var(--font-display)' }}>{val}</div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
            </div>
          ))}
        </div>
        {showTile('rank') && myRank && teammateCount > 1 && (
          <p className="text-xs mt-3 pt-3" style={{ color: 'var(--text-2)', borderTop: '1px solid var(--border)' }}>
            You're <strong style={{ color: 'var(--text)' }}>#{myRank} of {teammateCount}</strong> on the team for games attended.
          </p>
        )}
      </div>
      )}

      {/* ── Achievements ── */}
      {showTile('achievements') && badges.length > 0 && (
        <div className="zen-card p-5 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Award size={14} style={{ color: 'var(--puck)' }} />
              <h2 className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: 'var(--font-display)' }}>
                Achievements
              </h2>
            </div>
            <span className="text-xs font-bold" style={{ color: 'var(--puck)', fontFamily: 'var(--font-display)', letterSpacing: '0.1em' }}>
              {badges.length} UNLOCKED
            </span>
          </div>
          {currentStreak > 0 && (
            <div className="mb-4 p-3 rounded-xl flex items-center gap-3"
              style={{ background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.15)' }}>
              <div className="text-2xl">🔥</div>
              <div>
                <div className="font-bold text-sm" style={{ color: 'var(--fire, #ff6b35)' }}>
                  {currentStreak}-Game Streak
                </div>
                <div className="text-xs" style={{ color: 'var(--text-2)' }}>
                  Best: {maxStreak} · Attendance: {attendancePct}%
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {badges.map(b => (
              <div key={b.id} className={`achievement unlocked ${b.tier === 'gold' ? 'gold' : ''}`} title={b.desc}>
                <div className="text-3xl mb-1">{b.emoji}</div>
                <div className="text-xs font-bold"
                  style={{
                    color: b.tier === 'gold' ? 'var(--puck)' : 'var(--ice)',
                    fontFamily: 'var(--font-display)', lineHeight: 1.2,
                  }}>
                  {b.label}
                </div>
                <div className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)', fontSize: 9 }}>
                  {b.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Win streak callout (separate from current streak) ── */}
      {showTile('winPerformance') && (winStreak > 0 || maxWinStreak >= 3) && (
        <div className="zen-card p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Star size={14} style={{ color: 'var(--puck)' }} />
            <h2 className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: 'var(--font-display)' }}>
              Win Performance
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-3)', fontWeight: 600 }}>Current Win Streak</div>
              <div className="text-xl font-bold mt-1" style={{ color: winStreak > 0 ? 'var(--puck)' : 'var(--text-3)', fontFamily: 'var(--font-display)' }}>
                {winStreak}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-3)', fontWeight: 600 }}>Best Win Streak</div>
              <div className="text-xl font-bold mt-1" style={{ color: 'var(--puck)', fontFamily: 'var(--font-display)' }}>
                {maxWinStreak}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Team leaderboard (2026-07-27) — everyone's stats, no ranks. ── */}
      {teamBoardSection}

      <p className="text-xs text-center mt-4" style={{ color: 'var(--text-3)', opacity: 0.7 }}>
        Your stats only count games on this team. Switch teams in the sidebar to see another team's stats.
      </p>
    </div>
  );
}
